/**
 * File operations for CDP SDK.
 */

import { CDPSession, getSession } from '../session';

export class FileManager {
  constructor(private readonly session: CDPSession = getSession()) {}

  async listFiles(
    projectUuid: string,
    directory = '/',
    page = 1,
    size = 20,
  ): Promise<Record<string, any>> {
    const params = { projectUuid, directory, current: page, size };
    return this.session.get('/api/cdp/file/list', { params });
  }

  async uploadFile(
    projectUuid: string,
    file: File | Blob,
    directory = '/',
    description?: string,
  ): Promise<Record<string, any>> {
    const formData = new FormData();
    formData.append('file', file);
    const data: Record<string, any> = { projectUuid, directory };
    if (description) data.description = description;
    return this.session.post('/api/cdp/file/upload', {
      data: formData,
      params: data,
      headers: {},
    });
  }

  /** Download file as Blob. */
  async downloadFile(uuid: string, version?: number): Promise<Blob> {
    const params: Record<string, any> = {};
    if (version) params.version = version;
    return this.session.request('GET', `/api/cdp/file/download/${uuid}`, {
      params,
      responseType: 'blob',
    });
  }

  async deleteFile(uuid: string): Promise<Record<string, any>> {
    return this.session.delete(`/api/cdp/file/${uuid}`);
  }

  async getFileInfo(uuid: string): Promise<Record<string, any>> {
    return this.session.get(`/api/cdp/file/${uuid}`);
  }

  async createDirectory(
    projectUuid: string,
    path: string,
    description?: string,
  ): Promise<Record<string, any>> {
    const data: Record<string, any> = { projectUuid, path };
    if (description) data.description = description;
    return this.session.post('/api/cdp/file/mkdir', { data });
  }

  async deleteDirectory(
    projectUuid: string,
    path: string,
    recursive = false,
  ): Promise<Record<string, any>> {
    return this.session.post('/api/cdp/file/rmdir', {
      data: { projectUuid, path, recursive },
    });
  }

  async moveFile(
    uuid: string,
    targetDirectory: string,
    newName?: string,
  ): Promise<Record<string, any>> {
    const data: Record<string, any> = { uuid, targetDirectory };
    if (newName) data.newName = newName;
    return this.session.post('/api/cdp/file/move', { data });
  }

  async copyFile(
    uuid: string,
    targetDirectory: string,
    newName?: string,
  ): Promise<Record<string, any>> {
    const data: Record<string, any> = { uuid, targetDirectory };
    if (newName) data.newName = newName;
    return this.session.post('/api/cdp/file/copy', { data });
  }

  async listFileVersions(uuid: string): Promise<Record<string, any>[]> {
    const result = await this.session.get(`/api/cdp/file/version/${uuid}`);
    return result?.data ?? [];
  }

  async restoreFileVersion(uuid: string, version: number): Promise<Record<string, any>> {
    return this.session.post('/api/cdp/file/restore', { data: { uuid, version } });
  }

  /** Get text content of a file. */
  async getFileContent(uuid: string, encoding = 'utf-8'): Promise<string> {
    const resp = await this.session.request('GET', `/api/cdp/file/content/${uuid}`, {
      responseType: 'text',
    });
    return resp as unknown as string;
  }

  async updateFileContent(
    uuid: string,
    content: string,
    encoding = 'utf-8',
  ): Promise<Record<string, any>> {
    return this.session.post('/api/cdp/file/content', {
      data: { uuid, content, encoding },
    });
  }

  async uploadVector(
    projectUuid: string,
    file: File | Blob,
    vectorType: string,
    name?: string,
    description?: string,
  ): Promise<Record<string, any>> {
    const formData = new FormData();
    formData.append('file', file);
    const data: Record<string, any> = { projectUuid, vectorType };
    if (name) data.name = name;
    if (description) data.description = description;
    return this.session.post('/api/cdp/vector/upload', {
      data: formData,
      params: data,
      headers: {},
    });
  }

  async listVectors(
    projectUuid: string,
    vectorType?: string,
    page = 1,
    size = 20,
  ): Promise<Record<string, any>> {
    const options: Record<string, string> = { projectUuid };
    if (vectorType) options.vectorType = vectorType;
    const params = { current: page, size, options: JSON.stringify(options) };
    return this.session.get('/api/cdp/vector/page', { params });
  }

  async getVector(uuid: string): Promise<Record<string, any>> {
    return this.session.get(`/api/cdp/vector/${uuid}`);
  }

  async deleteVector(uuid: string): Promise<Record<string, any>> {
    return this.session.delete(`/api/cdp/vector/${uuid}`);
  }

  /** Download vector as Blob. */
  async downloadVector(uuid: string): Promise<Blob> {
    return this.session.request('GET', `/api/cdp/vector/download/${uuid}`, {
      responseType: 'blob',
    });
  }

  async uploadBackground(
    projectUuid: string,
    sceneUuid: string,
    file: File | Blob,
  ): Promise<Record<string, any>> {
    const formData = new FormData();
    formData.append('file', file);
    return this.session.post('/api/cdp/scene/background/upload', {
      data: formData,
      params: { projectUuid, sceneUuid },
      headers: {},
    });
  }

  async listBackgrounds(projectUuid: string, sceneUuid?: string): Promise<Record<string, any>[]> {
    const params: Record<string, any> = { projectUuid };
    if (sceneUuid) params.sceneUuid = sceneUuid;
    const result = await this.session.get('/api/cdp/scene/background/list', { params });
    return result?.data ?? [];
  }

  // --- Extended file APIs ---

  async getDxfFile(params?: Record<string, any>): Promise<Record<string, any>> {
    return this.session.get('/api/cdp/cdp_file_group/extend/getDxfFile', { params });
  }

  async saveDisplay(data: Record<string, any>): Promise<Record<string, any>> {
    return this.session.post('/api/cdp/cdp_file_group/extend/saveDisplay', { data });
  }

  async getDisplays(params?: Record<string, any>): Promise<Record<string, any>> {
    return this.session.get('/api/cdp/cdp_file_group/extend/getDisplays', { params });
  }

  async getDisplay(params?: Record<string, any>): Promise<Record<string, any>> {
    return this.session.get('/api/cdp/cdp_file_group/extend/getDisplay', { params });
  }

  async updateDisplay(data: Record<string, any>): Promise<Record<string, any>> {
    return this.session.post('/api/cdp/cdp_file_group/extend/updateDisplay', { data });
  }

  async uploadBim(file: File | Blob, params?: Record<string, any>): Promise<Record<string, any>> {
    const formData = new FormData();
    formData.append('file', file);
    return this.session.post('/api/cdp/cdp_file_group/extend/uploadBim', {
      data: formData,
      params,
      headers: {},
    });
  }

  async uploadCad(file: File | Blob, params?: Record<string, any>): Promise<Record<string, any>> {
    const formData = new FormData();
    formData.append('file', file);
    return this.session.post('/api/cdp/cdp_file_group/extend/uploadCad', {
      data: formData,
      params,
      headers: {},
    });
  }

  // --- System file / folder APIs ---

  async createFolder(data: Record<string, any>): Promise<Record<string, any>> {
    return this.session.post('/api/system/sys_folder/create', { data: { context: {}, data } });
  }

  async updateFolder(data: Record<string, any>): Promise<Record<string, any>> {
    return this.session.post('/api/system/sys_folder/update', { data: { context: {}, data } });
  }

  async hasFolderFiles(folderId: string): Promise<Record<string, any>> {
    return this.session.get(`/api/system/sys_folder/hasFiles/${folderId}`);
  }

  async deleteFolder(folderId: string): Promise<Record<string, any>> {
    return this.session.delete(`/api/system/sys_folder/delete/${folderId}`);
  }

  async uploadSystemFile(file: File | Blob, params?: Record<string, any>): Promise<Record<string, any>> {
    const formData = new FormData();
    formData.append('file', file);
    return this.session.post('/api/system/sys_file/upload', {
      data: formData,
      params,
      headers: {},
    });
  }

  async updateSystemFile(data: Record<string, any>): Promise<Record<string, any>> {
    return this.session.post('/api/system/sys_file/update', { data: { context: {}, data } });
  }

  async deleteSystemFile(fileId: string): Promise<Record<string, any>> {
    return this.session.delete(`/api/system/sys_file/delete/${fileId}`);
  }

  async downloadSystemFile(fileId: string): Promise<Blob> {
    return this.session.request('GET', `/api/system/sys_file/download/${fileId}`, {
      responseType: 'blob',
    });
  }

  async restoreSystemFile(fileId: string): Promise<Record<string, any>> {
    return this.session.post(`/api/system/sys_file/restore/${fileId}`);
  }

  async completelyDeleteFile(fileId: string): Promise<Record<string, any>> {
    return this.session.delete(`/api/system/sys_file/completelyDelete/${fileId}`);
  }

  async batchUpdateFiles(data: Record<string, any>): Promise<Record<string, any>> {
    return this.session.post('/api/system/sys_file/batchUpdate', { data: { context: {}, data } });
  }
}
